import { saveAs } from 'file-saver';
import { formatDateTimeInVenueTz } from './formatDateTime';

const EXPORT_PAGE_SIZE = 1000;

/*
 * This function is used to convert the date format from the API to a more readable format.
 * @param {string} date
 * @returns {string} - date in venue timezone
 */
const dateConverter = (date) => {
    if (!date) return '-';
    return formatDateTimeInVenueTz(date);
};

export default dateConverter;

/**
 * Custom mapping for data
 * @param {array} data array of objects to map
 * @param {string} responseData response data type
 * @returns array of objects
 */
export const customMapping = (data, responseData) => {
    const mapped = data.map((item) => {
        switch (responseData) {
            case 'seats': {
                const seat = {
                    Id: item.id,
                    Bookable: item.bookable ? 1 : 0,
                    Name: item.name,
                    X: item.svgInfo.x,
                    Y: item.svgInfo.y,
                    Rotation: item.svgInfo.rotation,
                    Scale: item.svgInfo.scale,
                    Model: item.svgInfo.model,
                };
                if (item.nameI18n) {
                    Object.keys(item.nameI18n).forEach((lang) => {
                        seat[`Name ${lang.toUpperCase()}`] = item.nameI18n[lang] || '';
                    });
                }
                return seat;
            }
            case 'timeslots':
                return {
                    Id: item.id,
                    'Start time': dateConverter(item.startTime),
                };
            case 'bookings':
                return {
                    Id: item.id,
                    Status: item.status,
                    Venue: item.venueName,
                    Timeslot: dateConverter(item.timeslot.startTime),
                    // rental slot marker - empty for non-rental bookings; the
                    // check-in export includes overlapping sibling rows. No show
                    // context here, so fall back to the raw slot key.
                    Period: item.timeslot?.slotKey ?? '',
                    'First name': item.firstName,
                    'Last name': item.lastName,
                    Phone: item.phone,
                    Email: item.email,
                };
            case 'cateringview': {
                const staticRow = {
                    Id: item.id,
                    Seat: item.ticketSeatName,
                    'First name': item.booking.firstName,
                    'Last name': item.booking.lastName,
                    Phone: item.booking.phone,
                    Email: item.booking.email,
                    Note: item.note,
                    'Checkin note': item.checkinNote,
                };
                const notesByLabel = new Map(
                    (item.booking.note || []).map((n) => [n.label, n.value]),
                );
                const optionalNotes = (item.ticketTypeCustomFields || [])
                    .filter((field) => field.showInCatering)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .reduce((acc, field) => {
                        const key = field.name in staticRow ? `${field.name} (custom)` : field.name;
                        acc[key] = notesByLabel.get(field.name) ?? '';
                        return acc;
                    }, {});
                return { ...staticRow, ...optionalNotes };
            }
            case 'checkinview':
                return {
                    Id: item.id,
                    Name: item.firstName + ' ' + item.lastName,
                    Seats: item.seatCount,
                    Email: item.email,
                    Phone: item.phone,
                    'Checked in': item.isCheckedIn ? 'Yes' : 'No',
                };
            default:
                return {};
        }
    });
    // cateringview can have dynamic columns per row; normalize to a shared
    // union of keys so every row aligns under the same headers.
    const needsNormalization = responseData === 'cateringview';
    const normalized = needsNormalization
        ? (() => {
              const keys = [...new Set(mapped.flatMap(Object.keys))];
              return mapped.map((row) => Object.fromEntries(keys.map((k) => [k, row[k] ?? ''])));
          })()
        : mapped;
    // only output unique lines
    return [
        ...new Set(
            normalized.map((item) => {
                return JSON.stringify(item);
            }),
        )
            .values()
            .map((item) => {
                return JSON.parse(item);
            }),
    ];
};

/**
 * Collect all data from an API endpoint
 * @param {object} data data object from API
 * @param {function} apiFunction direct Orval API function to call (page + pagesize)
 * @param {function} setProgress function to set progress
 * @param {string} responseData response data key name
 * @param {string} mappingName optional mapping name override
 * @returns array of objects
 */
export const collectAllData = async ({
    data,
    apiFunction,
    setProgress,
    responseData,
    mappingName,
    filterFn,
}) => {
    const allData = [];
    const totalData = data?.total;

    for (let i = 0; i < Math.ceil(totalData / EXPORT_PAGE_SIZE); i++) {
        const res = await apiFunction(i, EXPORT_PAGE_SIZE);
        // filterFn runs on the raw row, before customMapping rewrites it into
        // the export shape — callers that need to drop rows by a field the
        // mapping strips (e.g. ticketTypes on bookings) hook in here.
        res[`${responseData}`].forEach((e) => {
            if (!filterFn || filterFn(e)) allData.push(e);
        });
        setProgress((prevProgress) => prevProgress + (EXPORT_PAGE_SIZE / totalData) * 100);
    }

    // custom mapping based on mappingName
    return customMapping(allData, mappingName || responseData);
};

/**
 * Generate xlsx data from an array of objects
 * @param {array} data array of objects to convert to xlsx rows
 * @param {boolean} isWithoutHeader flag to include header or not
 * @returns header fields from object keys and rows from values
 */
export const generateXlsxData = (data, isWithoutHeader = false) => {
    if (!data || data.length === 0) {
        return [];
    }

    const newData = data;
    const headers = Object.keys(newData[0]);

    const rows = newData.map((element) => {
        return Object.values(element).map((value) => {
            if (Array.isArray(value)) {
                return value.map((e) => e.name).join('\n');
            }
            return value;
        });
    });

    return isWithoutHeader ? [...rows] : [headers, ...rows];
};

export const CURRENCY_FORMAT = '$#,##0.00';

export const centsToXlsxNumber = (cents) => (typeof cents === 'number' ? cents / 100 : '');

/**
 * Build a human-readable date-range label for an export header.
 * Falls back to a single date, or "All dates" when neither is set.
 */
export const buildDateRangeLabel = (dateFrom, dateTo) => {
    const formatDateLabel = (value) => {
        if (!value) return null;
        const parsed = new Date(`${value}T12:00:00`);
        return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleDateString();
    };
    const fromLabel = formatDateLabel(dateFrom);
    const toLabel = formatDateLabel(dateTo);
    return fromLabel && toLabel ? `${fromLabel} - ${toLabel}` : fromLabel || toLabel || 'All dates';
};

/**
 * Build a filename-safe date-range fragment for an export file name.
 */
export const buildFilenameRange = (dateFrom, dateTo) =>
    dateFrom && dateTo ? `${dateFrom}-to-${dateTo}` : dateFrom || dateTo || 'all-dates';

export const applyCurrencyFormat = (
    XLSX,
    ws,
    sheetData,
    columnIndices,
    format = CURRENCY_FORMAT,
) => {
    for (const col of columnIndices) {
        for (let row = 0; row < sheetData.length; row++) {
            const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
            const cell = ws[cellRef];
            if (cell && typeof cell.v === 'number') {
                cell.z = format;
            }
        }
    }
};

const XLSX_FILE_TYPE =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';

/**
 * Serialize a workbook to xlsx bytes and trigger a browser download.
 * @param {object} XLSX dynamically imported xlsx module
 * @param {object} wb workbook to write
 * @param {string} filename file name including the .xlsx extension
 */
export const writeWorkbookAndSave = (XLSX, wb, filename) => {
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const file = new Blob([excelBuffer], { type: XLSX_FILE_TYPE });
    saveAs(file, filename);
};

/**
 * Generate xlsx file and initiate download
 * @param {array} data array of objects to convert to xlsx rows
 * @param {string} name name of the file
 * @param {boolean} isWithoutHeader flag to include header or not
 */
export const exportToXlsx = async (data, name, isWithoutHeader = false) => {
    const XLSX = await import('xlsx');
    const ws = XLSX.utils.aoa_to_sheet(generateXlsxData(data, isWithoutHeader));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'SheetJS');
    writeWorkbookAndSave(XLSX, wb, `${name}.xlsx`);
};
